import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronsUpDown, Check, type LucideIcon } from 'lucide-react';
import baseapi from '../services/baseapi';

interface CompactDropdownItem {
    id?: string | number | null;
    [key: string]: any;
}

interface CompactSingleSelectApiDropdownProps {
    label?: string;
    value?: CompactDropdownItem | null;
    onChange: (item: CompactDropdownItem | null) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    apiEndpoint: string;
    icon?: LucideIcon | null;
    searchPlaceholder?: string;
    searchInputPlaceholder: string;
    error?: string;
    required?: boolean;
    displayProperty: string;
    secondaryDisplayProperty?: string;
    searchFields: string[];
    additionalQueries?: Record<string, any>;
    additionalParams?: Record<string, any>;
    defaultValue?: boolean;
    getDisplayText?: (item: CompactDropdownItem) => string;
    clearDisabled?: boolean;
    idProperty?: string;
    responseDataKey?: string;
    showSecondaryDisplayProperty?: boolean;
    buttonClassName?: string;
    labelTextSize?: string;
    menuWidth?: number | string;
}

const CompactSingleSelectApiDropdown = React.memo<CompactSingleSelectApiDropdownProps>(({
    label,
    value,
    onChange,
    disabled = false,
    placeholder = 'Select',
    className = '',
    apiEndpoint,
    icon: Icon = null,
    searchPlaceholder = 'Search',
    searchInputPlaceholder,
    error,
    required = false,
    displayProperty,
    secondaryDisplayProperty,
    searchFields,
    additionalQueries = {},
    additionalParams = {},
    defaultValue = false,
    getDisplayText,
    clearDisabled = false,
    idProperty = 'id',
    responseDataKey = 'data',
    showSecondaryDisplayProperty = true,
    buttonClassName = '',
    labelTextSize = 'text-[9px]',
    menuWidth,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [items, setItems] = useState<CompactDropdownItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [hasInitialLoad, setHasInitialLoad] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const prevIsOpen = useRef(false);
    const memoizedAdditionalQueries = useRef(additionalQueries);
    const memoizedAdditionalParams = useRef(additionalParams);

    useEffect(() => {
        if (JSON.stringify(memoizedAdditionalQueries.current) !== JSON.stringify(additionalQueries)) {
            memoizedAdditionalQueries.current = additionalQueries;
        }
    }, [additionalQueries]);

    useEffect(() => {
        if (JSON.stringify(memoizedAdditionalParams.current) !== JSON.stringify(additionalParams)) {
            memoizedAdditionalParams.current = additionalParams;
        }
    }, [additionalParams]);

    const getNestedProperty = useCallback((obj: any, path: string) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
    }, []);

    const getItemId = useCallback((item: CompactDropdownItem) => {
        return getNestedProperty(item, idProperty);
    }, [getNestedProperty, idProperty]);

    const getDisplayName = useCallback((item: CompactDropdownItem) => {
        if (getDisplayText) return getDisplayText(item);
        const displayValue = getNestedProperty(item, displayProperty);
        return displayValue?.toString() || getItemId(item)?.toString() || '';
    }, [getDisplayText, displayProperty, getNestedProperty, getItemId]);

    const getSecondaryDisplayName = useCallback((item: CompactDropdownItem) => {
        if (!secondaryDisplayProperty) return null;
        const secondaryValue = getNestedProperty(item, secondaryDisplayProperty);
        return secondaryValue?.toString() || null;
    }, [getNestedProperty, secondaryDisplayProperty]);

    const calculateDropdownPosition = useCallback(() => {
        if (!buttonRef.current) return;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 230;
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        const widthValue =
            typeof menuWidth === 'number'
                ? `${menuWidth}px`
                : menuWidth || `${buttonRect.width}px`;

        const style: React.CSSProperties = {
            width: widthValue,
            left: `${buttonRect.left}px`,
            position: 'fixed',
        };

        if (spaceBelow < dropdownHeight + 12 && spaceAbove > dropdownHeight + 12) {
            setDropdownPosition('top');
            style.bottom = `${windowHeight - buttonRect.top + 4}px`;
        } else {
            setDropdownPosition('bottom');
            style.top = `${buttonRect.bottom + 4}px`;
        }

        setMenuStyle(style);
    }, [menuWidth]);

    const updatePosition = useCallback(() => {
        if (isOpen) calculateDropdownPosition();
    }, [isOpen, calculateDropdownPosition]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    const debounce = useCallback((func: Function, delay: number) => {
        let timeoutId: number;
        return (...args: any[]) => {
            clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => func.apply(null, args), delay);
        };
    }, []);

    const handleAutoSelect = useCallback((itemsList: CompactDropdownItem[]) => {
        if (defaultValue && !value && itemsList.length > 0) onChange(itemsList[0]);
    }, [defaultValue, value, onChange]);

    const fetchItems = useCallback(async (search = '') => {
        if (showSearch && search.trim() === '') {
            setItems([]);
            return;
        }

        setIsLoading(true);
        try {
            const queryObj: Record<string, any> = { ...memoizedAdditionalQueries.current };

            if (search.trim()) {
                queryObj.$or = searchFields.map((field) => ({
                    [field]: { $ilike: `%${search}%` },
                }));
            }

            const params = {
                query: JSON.stringify(queryObj),
                limit: search.trim() ? 10 : 20,
                ...memoizedAdditionalParams.current,
            };

            const response = await baseapi.get(apiEndpoint, { params });

            if (response.data.status) {
                const transformedItems = response.data[responseDataKey] || [];
                setItems(transformedItems);
                if (!showSearch) handleAutoSelect(transformedItems);
            }
        } catch (error) {
            console.error(`Error fetching data from ${apiEndpoint}:`, error);
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    }, [apiEndpoint, searchFields, responseDataKey, showSearch, handleAutoSelect]);

    const debouncedSearch = useCallback(
        debounce((term: string) => fetchItems(term), 250),
        [fetchItems, debounce]
    );

    useEffect(() => {
        if (defaultValue && !hasInitialLoad) {
            fetchItems();
            setHasInitialLoad(true);
        }
    }, [defaultValue, hasInitialLoad, fetchItems]);

    useEffect(() => {
        if (isOpen && !prevIsOpen.current) {
            if (!defaultValue || hasInitialLoad) fetchItems();
        }
        prevIsOpen.current = isOpen;
    }, [isOpen, defaultValue, hasInitialLoad, fetchItems]);

    useEffect(() => {
        if (showSearch) {
            if (searchTerm.trim()) debouncedSearch(searchTerm);
            else setItems([]);
        }
    }, [searchTerm, showSearch, debouncedSearch]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                (!menuRef.current || !menuRef.current.contains(target))
            ) {
                setIsOpen(false);
                setShowSearch(false);
                setSearchTerm('');
            }
        };

        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
                setShowSearch(false);
                setSearchTerm('');
                buttonRef.current?.focus();
            }
        };

        if (isOpen) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    useEffect(() => {
        if (showSearch && isOpen && searchInputRef.current) {
            const timer = setTimeout(() => searchInputRef.current?.focus(), 60);
            return () => clearTimeout(timer);
        }
    }, [showSearch, isOpen]);

    const toggleDropdown = () => {
        if (disabled) return;
        calculateDropdownPosition();
        setIsOpen((prev) => !prev);
        if (isOpen) {
            setShowSearch(false);
            setSearchTerm('');
        }
    };

    const handleItemSelect = (item: CompactDropdownItem) => {
        onChange(item);
        setIsOpen(false);
        setShowSearch(false);
        setSearchTerm('');
    };

    const handleSearchToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowSearch(true);
        setTimeout(() => searchInputRef.current?.focus(), 40);
    };

    const handleClearSelection = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    return (
        <div className={`space-y-1 ${className}`} ref={dropdownRef}>
            {label && (
                <label className={`flex items-center gap-1.5 font-black text-slate-400 uppercase tracking-[0.18em] ${labelTextSize}`}>
                    {Icon && <Icon className="w-3 h-3 text-[#422568]" aria-hidden="true" />}
                    {label}
                    {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <div className="relative">
                <button
                    ref={buttonRef}
                    onClick={toggleDropdown}
                    disabled={disabled}
                    className={`relative w-full cursor-pointer rounded-lg border border-slate-200 bg-white pl-2.5 pr-7 text-left text-[11px] transition-all duration-200 hover:border-slate-300 focus:border-[#422568] focus:outline-none focus:ring-2 focus:ring-[#422568]/10 disabled:cursor-not-allowed disabled:opacity-50 h-8 ${buttonClassName}`}
                    type="button"
                >
                    <div className="flex items-center gap-1.5 h-full min-w-0">
                        <div className="flex-1 min-w-0 flex items-center">
                            {value && getDisplayName(value) ? (
                                <div className="min-w-0 flex flex-col justify-center">
                                    <span className="block truncate text-slate-800 font-bold leading-tight text-[11px]">
                                        {getDisplayName(value)}
                                    </span>
                                    {showSecondaryDisplayProperty && secondaryDisplayProperty && getSecondaryDisplayName(value) && (
                                        <span className="block truncate text-[9px] text-slate-400 font-bold leading-tight">
                                            {getSecondaryDisplayName(value)}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <span className="block truncate text-slate-400 font-bold text-[11px] leading-tight">
                                    {placeholder}
                                </span>
                            )}
                        </div>

                        {value && (
                            <button
                                onClick={handleClearSelection}
                                className="p-0.5 hover:bg-slate-100 rounded transition-colors shrink-0"
                                type="button"
                                disabled={clearDisabled}
                            >
                                <X className={`w-3 h-3 text-slate-400 hover:text-red-500 ${clearDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`} />
                            </button>
                        )}
                    </div>

                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronsUpDown className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#422568]' : ''}`} />
                    </span>
                </button>

                {error && (
                    <span className="text-red-500 text-[9px] font-black uppercase tracking-tight mt-1 flex items-center gap-1">
                        {error}
                    </span>
                )}

                {isOpen && createPortal(
                    <div
                        ref={menuRef}
                        className={`fixed z-[99999] overflow-hidden rounded-lg bg-white  border border-slate-200 animate-in fade-in-0 zoom-in-95 duration-100 ${dropdownPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                        style={menuStyle}
                    >
                        <div className="flex flex-col h-full">
                            {showSearch ? (
                                <div className="p-1.5 border-b border-slate-100 bg-slate-50/70">
                                    <div className="relative">
                                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            className="w-full bg-white border border-slate-200 rounded-md pl-7 pr-2 py-1.5 text-[11px] text-slate-800 font-bold placeholder-slate-400 focus:border-[#422568] focus:outline-none"
                                            placeholder={searchInputPlaceholder}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="p-1 border-b border-slate-100">
                                    <button
                                        onClick={handleSearchToggle}
                                        className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[11px] text-slate-400 font-bold hover:text-slate-800 hover:bg-slate-50 rounded-md transition-colors"
                                        type="button"
                                    >
                                        <Search className="w-3 h-3" />
                                        {searchPlaceholder}
                                    </button>
                                </div>
                            )}

                            <div className="overflow-y-auto overflow-x-hidden flex-1 max-h-[190px]">
                                {isLoading && items.length === 0 ? (
                                    <div className="p-4 text-center">
                                        <div className="w-4 h-4 border-2 border-[#422568]/20 border-t-[#422568] rounded-full animate-spin mx-auto mb-1.5" />
                                        <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">Loading...</span>
                                    </div>
                                ) : items.length > 0 ? (
                                    items.map((item) => {
                                        const itemId = getItemId(item);
                                        const isSelected = value && getItemId(value) === itemId;
                                        const displayName = getDisplayName(item);
                                        const secondaryDisplayName = getSecondaryDisplayName(item);

                                        return (
                                            <button
                                                key={itemId}
                                                onClick={() => handleItemSelect(item)}
                                                className={`w-full relative cursor-pointer select-none py-1.5 px-2.5 text-[11px] text-left transition-all duration-150 ${isSelected ? 'bg-[#422568] text-[#422568]' : 'text-slate-600 hover:bg-slate-50'}`}
                                                type="button"
                                            >
                                                <div className="flex items-center gap-2">
                                                    {Icon && (
                                                        <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#422568]' : 'bg-slate-100'}`}>
                                                            <Icon className={`w-2.5 h-2.5 ${isSelected ? 'text-white' : 'text-slate-400'}`} />
                                                        </div>
                                                    )}

                                                    <div className="min-w-0 flex-1">
                                                        <span className={`block truncate ${isSelected ? 'font-black' : 'font-bold'}`}>
                                                            {displayName}
                                                        </span>
                                                        {secondaryDisplayProperty && secondaryDisplayName && (
                                                            <span className="block truncate text-[9px] text-slate-400 font-bold mt-0.5">
                                                                {secondaryDisplayName}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {isSelected && (
                                                    <span className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-[#422568]">
                                                        <Check className="h-3.5 w-3.5" />
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="p-4 text-center">
                                        <p className="text-[10px] text-gray-400 font-medium">
                                            {showSearch && searchTerm.trim() === ''
                                                ? 'Type to search...'
                                                : showSearch
                                                    ? 'No results found'
                                                    : 'No data available'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
});

export default CompactSingleSelectApiDropdown;